// "Eligibility" card of the aircraft form: range, cruise speed, ferry distance, homebase, deadline, blackout ranges.
import type { FormInstance } from 'antd';
import { Button, Card, Col, DatePicker, Divider, Form, Input, Row, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import { AirportSelect } from '../../components/AirportSelect';
// SHAR-917 - numeric fields that refuse letters and symbols as they are typed.
import NumberInput from '../../components/NumberInput';
import { updateRange, type Range } from './aircraftFormRows';
import { LIMITS } from '../../data/limits';

export function EligibilityCard({
  form,
  ranges,
  setRanges,
}: {
  form: FormInstance;
  ranges: Range[];
  setRanges: React.Dispatch<React.SetStateAction<Range[]>>;
}) {
  return (
    <Card title="Eligibility" style={{ marginBottom: 16 }}>
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Form.Item
            name="maxRangeNm"
            label="Maximum range (NM)"
          >
            <NumberInput min={50} max={20000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="cruiseSpeedKnots"
            label="Cruise speed (knots)"
          >
            <NumberInput min={100} max={700} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="maxFerryDistanceNm"
            label="Max ferry distance (NM)"
          >
            <NumberInput min={0} max={5000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="homebaseAirportCode"
            label="Homebase airport"
          >
            <AirportSelect
              onChange={(code, label) => {
                form.setFieldValue('homebaseAirportCode', code);
                form.setFieldValue('homebaseAirportName', label);
              }}
            />
          </Form.Item>
          <Form.Item name="homebaseAirportName" hidden>
            <Input />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            name="confirmationDeadlineDays"
            label="Cancellation deadline (days before departure)"
          >
            <NumberInput min={0} max={60} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Divider>Blackout date ranges</Divider>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {ranges.map((r, i) => (
          <Row gutter={8} align="middle" key={i}>
            <Col xs={24} md={6}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="From"
                value={r.fromDate}
                onChange={(v) => updateRange(setRanges, i, { fromDate: v })}
              />
            </Col>
            <Col xs={24} md={6}>
              <DatePicker
                style={{ width: '100%' }}
                placeholder="To"
                value={r.toDate}
                onChange={(v) => updateRange(setRanges, i, { toDate: v })}
              />
            </Col>
            <Col xs={24} md={10}>
              <Input
                placeholder="Reason (optional)"
                // Admin regression 6 Oct — the API's free-text limit (create and edit).
                maxLength={LIMITS.text}
                value={r.reason}
                onChange={(e) => updateRange(setRanges, i, { reason: e.target.value })}
              />
            </Col>
            <Col xs={24} md={2}>
              <Button
                icon={<DeleteOutlined />}
                danger
                onClick={() => setRanges((rs) => rs.filter((_, j) => j !== i))}
              />
            </Col>
          </Row>
        ))}
        <Button
          icon={<PlusOutlined />}
          onClick={() =>
            setRanges((rs) => [...rs, { fromDate: null, toDate: null, reason: '' }])
          }
        >
          Add blackout range
        </Button>
      </Space>
    </Card>
  );
}
